/**
 * Card — surface with border + subtle shadow. Neutral host container
 * for the Vault's GI entries, stats bar, recent-activity list, and
 * detail page.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/utils/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padded?: boolean;
  interactive?: boolean;
  children: ReactNode;
}

export function Card({
  padded = true,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-surface shadow-subtle',
        padded ? 'p-5' : '',
        interactive
          ? 'cursor-pointer transition-shadow hover:shadow-raised hover:border-border-strong'
          : '',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mb-3 flex items-start justify-between gap-3', className)}>{children}</div>;
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={cn('text-sm font-semibold text-ink', className)}>{children}</h2>;
}

export function CardSubtitle({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-xs text-ink-muted', className)}>{children}</p>;
}
